/* FILE: ./frontend/src/features/canada/components/RfqManager.jsx */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from '../../../lib/supabase';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

export default function RfqManager() {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmails, setInviteEmails] = useState({}); 
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    fetchRfqs();
  }, []);

  const fetchRfqs = async () => {
    try {
      const response = await axios.get(`${API_BASE}/rfqs`);
      
      // ⚡ THE FIX: Filter out support tickets immediately after fetching
      const allRfqs = response.data || [];
      const procurementRfqsOnly = allRfqs.filter(rfq => rfq.hs_code !== '000000' && rfq.type !== 'support');
      
      setRfqs(procurementRfqsOnly);
    } catch (err) {
      console.error("Error fetching RFQs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async (rfqId) => {
    const email = inviteEmails[rfqId];
    if (!email || !email.includes('@')) return alert("Please enter a valid email.");

    setSendingId(rfqId);
    try {
      await axios.post(`${API_BASE}/rfqs/invite`, {
        rfqId: rfqId,
        email: email
      });

      setInviteEmails(prev => ({ ...prev, [rfqId]: '' }));
      fetchRfqs();
      
      alert(`✅ Official Invitation Email sent to ${email}!`);

    } catch (err) {
      console.error("Invite Error:", err);
      alert("Failed to send invite: " + (err.response?.data?.error || err.message));
    } finally {
      setSendingId(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500 animate-pulse">Loading RFQ Engine...</div>;

  return (
    <div className="space-y-6">
      
      <div className="flex justify-between items-center border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Concierge ITB Engine</h2>
          <p className="text-sm text-gray-500">Manage buyer RFQs and invite off-platform suppliers to bid.</p>
        </div>
        <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">
          {rfqs.length} Total Requests
        </span>
      </div>

      {rfqs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <span className="text-3xl mb-3 block">📭</span>
          <p className="text-gray-500 font-medium">No RFQs in the system.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {rfqs.map(rfq => (
            <div key={rfq.id} className={`bg-white border ${rfq.status === 'accepted' ? 'border-green-200' : 'border-gray-200'} rounded-xl shadow-sm overflow-hidden flex flex-col md:flex-row`}>
              
              {/* Left Side: RFQ Details */}
              <div className="p-6 flex-1 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-wider bg-blue-100 px-2 py-1 rounded mr-2">
                      HS: {rfq.hs_code || 'N/A'}
                    </span>
                    {rfq.status === 'accepted' && (
                        <span className="text-xs font-bold text-green-800 uppercase tracking-wider bg-green-100 px-2 py-1 rounded">
                            🔒 Awarded
                        </span>
                    )}
                    <h3 className="text-xl font-extrabold text-gray-900 mt-2">
                      {Number(rfq.quantity).toLocaleString()} {rfq.uom}
                    </h3>
                  </div>
                  <div className="text-right">
                      <span className="bg-gray-200 text-gray-700 text-xs font-bold px-2 py-1 rounded block">
                        Dest: {rfq.destination_province}
                      </span>
                      {/* ⚡ FIX: Added Safety Fallback for missing buyer_user_id */}
                      <span className="text-[9px] font-mono text-gray-400 mt-2 block" title="Buyer User ID">
                          Buyer: {rfq.buyer_user_id ? rfq.buyer_user_id.substring(0,8) : 'Unknown'}...
                      </span>
                  </div>
                </div>
                <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-600 whitespace-pre-wrap">
                  {rfq.technical_specs || rfq.specs_text || "No additional technical specifications provided."}
                </div>
                <p className="text-xs text-gray-400 mt-4">
                  Requested on: {new Date(rfq.created_at).toLocaleDateString()}
                </p>
              </div>

              {/* Right Side: The Invite Engine */}
              <div className="p-6 flex-1 bg-white">
                
                {rfq.status === 'accepted' ? (
                    <div className="h-full flex flex-col items-center justify-center p-6 bg-green-50 border border-green-100 rounded-xl text-center">
                        <span className="text-4xl mb-2">🤝</span>
                        <h4 className="font-black text-green-900 tracking-wide uppercase">Negotiation Closed</h4>
                        <p className="text-sm text-green-700 mt-1">This RFQ has been successfully awarded to a supplier. No further invites can be sent.</p>
                    </div>
                ) : (
                    <>
                        <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span>📨</span> Send Blind Invites
                        </h4>
                        
                        <div className="flex gap-2 mb-6">
                        <input 
                            type="email" 
                            placeholder="supplier@company.ca"
                            className="flex-1 rounded border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500 bg-gray-50"
                            value={inviteEmails[rfq.id] || ''}
                            onChange={e => setInviteEmails(prev => ({...prev, [rfq.id]: e.target.value}))}
                        />
                        <button 
                            onClick={() => handleSendInvite(rfq.id)}
                            disabled={sendingId === rfq.id}
                            className="bg-gray-900 text-white px-4 py-2 rounded text-sm font-bold hover:bg-gray-800 disabled:opacity-50 transition-colors"
                        >
                            {sendingId === rfq.id ? 'Sending...' : 'Invite'}
                        </button>
                        </div>

                        {/* Sent Invitations Log */}
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 border-b border-gray-100 pb-2">
                        Invitation Status
                        </h4>
                        {rfq.rfq_invitations && rfq.rfq_invitations.length > 0 ? (
                        <ul className="space-y-2">
                            {rfq.rfq_invitations.map(inv => (
                            <li key={inv.id} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded border border-gray-100">
                                <span className="text-gray-700 font-medium truncate pr-2">{inv.recipient_email}</span>
                                <span className={`text-[10px] font-bold px-2 py-1 rounded shrink-0 ${
                                inv.status === 'viewed' ? 'bg-amber-100 text-amber-800' :
                                inv.status === 'registered' ? 'bg-green-100 text-green-800' :
                                'bg-blue-50 text-blue-600'
                                }`}>
                                {inv.status.toUpperCase()}
                                </span>
                            </li>
                            ))}
                        </ul>
                        ) : (
                        <p className="text-sm text-gray-400 italic">No suppliers invited yet.</p>
                        )}
                    </>
                )}
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
}